import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useParams, useNavigate } from 'react-router-dom';
import { EventType } from '../../types/Event';
import { useAuth } from '../../contexts/AuthContext';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkInput from '../common/SparkInput';
import SparkHeading from '../common/SparkHeading';
import SparkFooter from '../common/SparkFooter';

// Extend EventType to allow editing eventDate as string or Timestamp
type EditableEvent = Omit<EventType, 'eventDate'> & {
  eventDate?: Timestamp | string;
};

const EditEvent: React.FC = () => {
  const { user } = useAuth();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [eventData, setEventData] = useState<Partial<EditableEvent>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !eventId) return;

    const fetchEvent = async () => {
      setLoading(true);
      try {
        const ref = doc(db, 'events', eventId);
        const snapshot = await getDoc(ref);

        if (!snapshot.exists()) {
          setError('Event not found.');
        } else {
          const data = snapshot.data() as EventType;
          setEventData({
            ...data,
            eventDate: data.eventDate, // Timestamp
          });
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load event data.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [user, eventId]);

  const handleSave = async () => {
    if (!user || !eventId) return;

    setSaving(true);
    setError(null);

    try {
      const ref = doc(db, 'events', eventId);

      // 🔧 convert if the user edited the date field
      const normalizedDate =
        typeof eventData.eventDate === 'string'
          ? Timestamp.fromDate(new Date(eventData.eventDate))
          : eventData.eventDate; // already a Timestamp or undefined

      // Build a safe patch and never send `undefined`
      const patch: Record<string, any> = {
        title: eventData.title ?? '',
        description: eventData.description ?? '',
        location: eventData.location ?? '',
        eventTime: eventData.eventTime ?? '',
        updatedAt: serverTimestamp(),
      };

      // Only include eventDate if present AND valid
      if (eventData.eventDate) {
        let ts: Timestamp | null = null;

        if (typeof eventData.eventDate === 'string' && eventData.eventDate.trim()) {
          const d = new Date(eventData.eventDate);
          if (!isNaN(d.getTime())) ts = Timestamp.fromDate(d);
        } else if (eventData.eventDate instanceof Timestamp) {
          ts = eventData.eventDate;
        }

        if (ts) patch.eventDate = ts;
      }

      await updateDoc(ref, patch);


      alert('Event updated!');
      navigate(`/event/${eventId}`);
    } catch (err: any) {
      console.error(err);
      setError('Failed to update event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SparkLayout>
        <SparkCard>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              color: 'var(--color-text-primary)',
            }}
          >
            <div className="spark-spinner" />
            <span style={{ marginLeft: '1rem' }}>Loading event...</span>
          </div>
        </SparkCard>
      </SparkLayout>
    );
  }

  if (error) {
    return (
      <SparkLayout>
        <SparkCard>
          <div className="spark-message spark-message--error">{error}</div>
        </SparkCard>
      </SparkLayout>
    );
  }

  return (
    <SparkLayout>
      <SparkCard>
        <SparkHeading level="h1" glow>
          Edit Event
        </SparkHeading>

        {error && <div className="spark-message spark-message--error">{error}</div>}

        <SparkInput
          label="Title"
          id="title"
          value={eventData.title || ''}
          onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
        />

        <SparkInput
          label="Description"
          id="description"
          type="textarea"
          value={eventData.description || ''}
          onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
        />

        <SparkInput
          label="Location"
          id="location"
          value={eventData.location || ''}
          onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
        />

        <SparkInput
          label="Event Date"
          id="eventDate"
          type="date"
          value={
            eventData.eventDate
              ? typeof eventData.eventDate === 'string'
                ? eventData.eventDate
                : eventData.eventDate.toDate().toISOString().split('T')[0]
              : ''
          }
          onChange={(e) => setEventData({ ...eventData, eventDate: e.target.value })}
        />

        <SparkInput
          label="Event Time"
          id="eventTime"
          type="time"
          value={eventData.eventTime || ''}
          onChange={(e) => setEventData({ ...eventData, eventTime: e.target.value })}
        />

        <SparkFooter>
          <SparkButton onClick={() => navigate(`/event/${eventId}`)}>
            CANCEL
          </SparkButton>
          <SparkButton onClick={handleSave} disabled={saving} variant="violet">
            {saving ? 'SAVING...' : 'SAVE CHANGES'}
          </SparkButton>
        </SparkFooter>
      </SparkCard>
    </SparkLayout>
  );
};

export default EditEvent;
